import { Editor } from "@ckeditor/ckeditor5-core";
import { ModelElement, ViewDowncastWriter } from "@ckeditor/ckeditor5-engine";
import type { Mention } from "@ckeditor/ckeditor5-mention";

/**
 * Interface for mention attribute value in the model
 */
interface MentionAttributeValue {
  id: string;
  uid: string;
  userId?: string;
  link?: string;
  [key: string]: unknown;
}

/**
 * Configuration constants for mention customization
 */
const MENTION_CONFIG = {
  VIEW_ELEMENT_NAME: "a",
  VIEW_CLASS: "mention",
  MODEL_ATTRIBUTE_KEY: "mention",
  CONVERTER_PRIORITY: "high" as const,
  DOWNCAST_PRIORITY: 20,
  TARGET: "_blank",
} as const;

/**
 * Data attributes used in the mention view element
 */
const DATA_ATTRIBUTES = {
  MENTION: "data-mention",
  USER_ID: "data-user-id",
  HREF: "href",
} as const;

/**
 * Creates the upcast converter value function for mentions
 */
function createUpcasterValueConverter(editor: Editor) {
  return (viewItem: ModelElement) => {
    const mentionPlugin = editor.plugins.get("Mention") as Mention;
    
    // Extract attributes from the view element
    const href = viewItem.getAttribute(DATA_ATTRIBUTES.HREF) as string;
    const userId = viewItem.getAttribute(DATA_ATTRIBUTES.USER_ID) as string;
    
    // Create mention attribute using the official helper method
    const mentionAttribute = mentionPlugin.toMentionAttribute(viewItem, {
      link: href,
      userId: userId,
    });

    return mentionAttribute;
  };
}

/**
 * Creates the downcast converter view function for mentions
 */
function createDowncasterViewConverter() {
  return (modelAttributeValue: MentionAttributeValue, { writer }: { writer: ViewDowncastWriter }) => {
    // Guard against empty or invalid mention attributes
    if (!modelAttributeValue?.id) {
      return null;
    }

    // Build href URL for mention link
    const hrefUrl = modelAttributeValue.userId 
      ? `#type=mention&data=${modelAttributeValue.userId}`
      : `#type=mention&data=${modelAttributeValue.id}`;

    // Create view element attributes
    const viewAttributes = {
      class: MENTION_CONFIG.VIEW_CLASS,
      [DATA_ATTRIBUTES.MENTION]: modelAttributeValue.id,
      [DATA_ATTRIBUTES.HREF]: hrefUrl,
      target: MENTION_CONFIG.TARGET,
    };

    // Add user ID if available
    if (modelAttributeValue.userId) {
      viewAttributes[DATA_ATTRIBUTES.USER_ID] = modelAttributeValue.userId;
    }

    // Create and return the attribute element
    return writer.createAttributeElement(
      MENTION_CONFIG.VIEW_ELEMENT_NAME,
      viewAttributes,
      {
        priority: MENTION_CONFIG.DOWNCAST_PRIORITY,
        // Prevent merging mentions together using unique identifier
        id: modelAttributeValue.uid,
      }
    );
  };
}

/**
 * Configures custom mention conversion for CKEditor
 * 
 * This function sets up both upcast and downcast converters to handle
 * mention elements with custom attributes like user IDs and links.
 * 
 * @param editor - The CKEditor instance to configure
 */
export function MentionCustomization(editor: Editor): void {
  // Configure upcast conversion: View → Model
  editor.conversion.for("upcast").elementToAttribute({
    view: {
      name: MENTION_CONFIG.VIEW_ELEMENT_NAME,
      classes: MENTION_CONFIG.VIEW_CLASS,
      attributes: {
        [DATA_ATTRIBUTES.MENTION]: true,
        [DATA_ATTRIBUTES.HREF]: true,
        [DATA_ATTRIBUTES.USER_ID]: true,
      },
    },
    model: {
      key: MENTION_CONFIG.MODEL_ATTRIBUTE_KEY,
      value: createUpcasterValueConverter(editor),
    },
    converterPriority: MENTION_CONFIG.CONVERTER_PRIORITY,
  });

  // Configure downcast conversion: Model → View
  editor.conversion.for("downcast").attributeToElement({
    model: MENTION_CONFIG.MODEL_ATTRIBUTE_KEY,
    view: createDowncasterViewConverter(),
    converterPriority: MENTION_CONFIG.CONVERTER_PRIORITY,
  });
}
